import type { ReactNode } from 'react';

/*
 * The categorical palette for chart series.
 *
 * The six avatar tones, not the `type-*` family: a gesprekstype colour means
 * one specific thing (green *is* functioneringsgesprek), so borrowing it for
 * "the second line in this chart" would make an unrelated series look like a
 * conversation type. The avatar tones were already picked to be six deep,
 * mutually distinguishable colours on a light surface, which is exactly the
 * job — and `accent` is deliberately absent: 1.3:1 on white is a highlight,
 * not a data mark.
 *
 * Order is the assignment order. A series without an explicit `color` takes
 * the next one, wrapping after six.
 */
export const CHART_PALETTE = ['moss', 'navy', 'teal', 'plum', 'berry', 'ochre'] as const;

export type ChartColor = (typeof CHART_PALETTE)[number];

export interface ChartSeries {
  /** Names the line or the bar group — shown in the legend and the tooltip. */
  label: string;
  /** One value per entry in `labels`. */
  data: number[];
  /** Overrides the palette slot this series would otherwise take. */
  color?: ChartColor;
}

/** What LineChart and BarChart share. */
export interface ChartBaseProps {
  series: ChartSeries[];
  /** The x axis, one entry per index in every series' `data`. */
  labels: string[];
  /** Height of the plot area in pixels. The width is the container's. */
  height?: number;
  /** Formats the y axis ticks and the tooltip value, e.g. `(v) => \`$${v}\``. */
  yFormat?: (value: number) => string;
  /** Formats the x axis ticks, e.g. a raw ISO day into "12 Jun". */
  xFormat?: (label: string, index: number) => string;
  /** Defaults to on for more than one series, off for a single one. */
  showLegend?: boolean;
  /*
   * What the chart shows. Required: a canvas is opaque to a screen reader, so
   * this and the summary underneath it are the whole chart for anyone not
   * looking at it.
   */
  ariaLabel: string;
  /** Shown instead of the chart when no series carries a value. */
  emptyTitle?: ReactNode;
  className?: string;
}

export function seriesColor(
  palette: Record<ChartColor, string>,
  series: ChartSeries,
  index: number,
): string {
  return palette[series.color ?? CHART_PALETTE[index % CHART_PALETTE.length]];
}
